/**
 * @file index.tsx
 *
 * @fileoverview Shows a contact page layout for an anonymous users.
 */

import { useMutation } from '@apollo/client';
import { useRouter } from 'next/router';
import React, { useContext, useState } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import SEND_CONTACT_MESSAGE from '~/jobiqo/components/Contact/graphql/mutations/sendContactMessage.gql';
import ContactView from '~/jobiqo/components/Contact/view/ContactView';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { FlagsService, useFlags } from '~/jobiqo/hooks/flags';
import { getAPIEntityGroupValues } from '~/jobiqo/utils/config';
import { useGdprConsentContext } from '../GDPRConsent/hooks/useGdprConsentContext';

interface FormValues {
  name: string;
  email: string;
  subject: string;
  body: string;
  copy: boolean;
  attachments: File[];
}

export function Contact() {
  const intl = useIntl();
  const { APP } = useFrontendConfig();
  const flagService = useFlags();
  const { user } = useContext(UserContext);
  const router = useRouter();
  const [formKey, setFormKey] = useState(0);
  const [fieldErrorList, setFieldErrorList] = useState<any[]>([]);
  const { createGdprUserConsent } = useGdprConsentContext();
  const userContext: UserContextInterface = useContext(UserContext);
  const CONTACT_FORM = { ...APP.FIELD_INFO.contact_form };
  const CONTACT_FORM_REDIRECT = APP.CONTACT_FORM_SUBMIT_REDIRECT;

  const [sendContactMessage] = useMutation(SEND_CONTACT_MESSAGE, {
    onCompleted(completeData) {
      const errors = completeData.sendContactMessage?.errors ?? [];
      if (errors.length === 0) {
        router
          .push(
            !!user?.id
              ? CONTACT_FORM_REDIRECT.logged_in
              : CONTACT_FORM_REDIRECT.anonymous,
          )
          .then(() => handleSendContactSuccess(flagService, intl));
      } else {
        // Check if any errors have a property_path (field-level errors).
        const fieldErrors = errors.filter((e) => e?.property_path);
        if (fieldErrors.length > 0) {
          setFieldErrorList(fieldErrors);
        } else {
          handleSendContactError(flagService, intl);

          // Reset the form.
          setFormKey(formKey + 1);

          // Scroll to the top of the page smoothly.
          scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        }
      }
    },
  });

  return (
    <ContactView
      key={formKey}
      fieldErrorList={fieldErrorList}
      onSubmit={(formData) => {
        setFieldErrorList([]);
        const { body, ...rest } = getAPIEntityGroupValues(
          CONTACT_FORM,
          formData,
        ) as FormValues;

        if (userContext.user?.email || formData.mail) {
          // Create GPDR consent for the user.
          createGdprUserConsent(formData.mail ?? userContext.user?.email);
        }

        sendContactMessage({
          variables: {
            contactForm: 'FEEDBACK',
            data: {
              ...rest,
              message: body,
            },
          },
        });
      }}
    />
  );
}
export default Contact;

function handleSendContactError(flagService: FlagsService, intl: IntlShape) {
  flagService.addFlag({
    type: 'danger',
    text: intl.formatMessage({
      id: 'contact_error',
      defaultMessage:
        'There was an error sending your message. Please try again later.',
    }),
  });
}

function handleSendContactSuccess(flagService: FlagsService, intl: IntlShape) {
  flagService.addFlag({
    type: 'success',
    text: intl.formatMessage({
      id: 'contact_success',
      defaultMessage: 'Your message was sent to the site administrator',
    }),
  });
}
